import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { FormattedMessage } from "react-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthFormCard } from "@/components/AuthFormCard";
import { usePathModeStore } from "@/lib/stores/pathModeStore";
import { useTrainerAuthRedirect } from "@/lib/hooks/useTrainerAuthRedirect";
import type { PathMode } from "@/lib/types/pathMode";
import pathImage from "@/assets/exercise-background-photos/welcome-page-2.png";

export const Route = createFileRoute("/path")({
  component: PathSelector,
});

const options: Array<{ mode: PathMode; titleId: string; descriptionId: string }> =
  [
    {
      mode: "SELF_HELP",
      titleId: "path.helpYourself.title",
      descriptionId: "path.helpYourself.description",
    },
    {
      mode: "TRAINER",
      titleId: "path.helpOthers.title",
      descriptionId: "path.helpOthers.description",
    },
  ];

function PathSelector() {
  const navigate = useNavigate();
  const setMode = usePathModeStore((s) => s.setMode);
  const { setAuthOrigin } = useTrainerAuthRedirect();

  const handleSelect = (mode: PathMode) => {
    setMode(mode);
    setAuthOrigin(null);
    if (mode === "TRAINER") {
      navigate({ to: "/trainer/auth" });
    } else {
      navigate({ to: "/audience" });
    }
  };

  return (
    <div className="flex min-h-screen flex-col relative bg-background">
      <AuthHeader
        image={pathImage}
        imageAlt="Choose your path"
        title={<FormattedMessage id="path.heading" />}
        subtitle={<FormattedMessage id="path.subtitle" />}
        overlay="light"
      />

      <AuthFormCard>
        <div className="flex flex-col gap-4">
          {options.map((option, index) => (
            <Button
              key={option.mode}
              variant="outline"
              size="default"
              type="button"
              className={cn(
                "w-full py-9 border-0 flex flex-col gap-1",
                index === 0
                  ? "bg-primary hover:bg-primary/90"
                  : "bg-secondary hover:bg-secondary/90",
              )}
              onClick={() => handleSelect(option.mode)}
            >
              <span
                className={cn(
                  "text-body font-medium",
                  index === 0 ? "text-white" : "text-dark-gray",
                )}
              >
                <FormattedMessage id={option.titleId} />
              </span>
              <span
                className={cn(
                  "text-micro font-normal text-wrap text-center px-2",
                  index === 0 ? "text-white/85" : "text-dark-gray/80",
                )}
              >
                <FormattedMessage id={option.descriptionId} />
              </span>
            </Button>
          ))}
        </div>
      </AuthFormCard>
    </div>
  );
}
